import {useLegacyGrpcRequestCallback} from '@aboutyou/data-fetcher';
import {CheckoutV1_linkListLazy} from '@aboutyou/grpc-clients/grips/protos/checkout/checkout_v1.grpc.lazy';
import {LinkListV1Request_ListType} from '@aboutyou/grpc-clients/grips/protos/checkout/checkout_v1.types';
import {Log} from '@aboutyou/mlm';
import {
  useBasketCount,
  useUserTokenOrSessionId,
  useWishlistCount,
} from '@aboutyou/user';
import {useCallback} from 'react';

export const useLinkGuestSessionToCustomerSession = () => {
  const linkListCallback = useLegacyGrpcRequestCallback(
    CheckoutV1_linkListLazy,
  );
  const previousGuestSession = useUserTokenOrSessionId();

  const wishlistCount = useWishlistCount().count;
  const basketCount = useBasketCount();

  return useCallback(
    async (apiToken: string) => {
      if (!basketCount && !wishlistCount) return; // no need to try to merge if both are empty

      // If we don't have a session and we're not waiting for it to load anymore, we can fall back to the default empty wishlist state
      // If we don't have a session and we're not waiting for it to load anymore, we can fall back to the default empty wishlist state
      if (!previousGuestSession.data || previousGuestSession.isLoading) {
        Log.error(
          '@aboutyou/logic-components',
          'Failed to merge mobile wishlist and basket. Reason: sessionId not available',
        );

        throw new Error('Missing token or session id');
      }

      const linkList = async (listType: LinkListV1Request_ListType) => {
        await linkListCallback.mutateAsync({
          listType,
          previousGuestSession: {
            sessionId: previousGuestSession.data.resolvedId,
          },
          session: {
            authToken: apiToken,
          },
        });
      };

      const basketMerge = async () => {
        if (!basketCount) return;

        try {
          await linkList(LinkListV1Request_ListType.BASKET);
          Log.compare({
            actionName: 'BasketMergeEvent',
            success: true,
            attributes: {},
          });
        } catch (error) {
          Log.compare({
            actionName: 'BasketMergeEvent',
            success: false,
            attributes: {
              reason: error.message,
              code: error.code,
              metadata: error,
            },
          });
        }
      };

      const wishlistMerge = async () => {
        if (!wishlistCount) return;

        try {
          await linkList(LinkListV1Request_ListType.WISHLIST);
          Log.compare({
            actionName: 'WishlistMergeEvent',
            success: true,
            attributes: {},
          });
        } catch (error) {
          Log.compare({
            actionName: 'WishlistMergeEvent',
            success: false,
            attributes: {
              reason: error.message,
              code: error.code,
              metadata: error,
            },
          });
        }
      };

      await Promise.all([basketMerge(), wishlistMerge()]);
    },
    [
      basketCount,
      wishlistCount,
      previousGuestSession.data,
      previousGuestSession.isLoading,
      linkListCallback,
    ],
  );
};
